// 狀態文字讀字典 —— 基金 / 專案 / 投資者 / 夥伴 / KYC 五種狀態的中文標籤。
//
// 2026-10-27 使用者:「改成畫面上的狀態文字讀字典，讓字典可以改標籤」。 在這之前
// 狀態文字全部寫死在 StatusBadge.tsx 的對照表，字典頁改了不會有任何效果。
//
// 為什麼是一個小 store 而不是每個徽章各自 useQuery:
//   statusLabel() 是純函式, 被 tooltip / 句子 / 下拉選項到處呼叫, 叫不了 hook。
//   所以由 <StatusDictionaryLoader /> (掛在 Layout, 登入後才有) 把五份字典抓下來
//   寫進這裡, statusLabel() 直接查; 要在字典回來時重新 render 的元件呼叫
//   useStatusDictVersion() 訂閱。 字典還沒回來 (或失敗) 時 statusLabel 照舊用
//   StatusBadge 的寫死對照表 —— 那份現在只是備援, 跟 V495 對齊後的內建標籤逐字相同。
//
// 快取 key 對齊 useDictOptions / ReferenceSelect → 同一份字典全站只抓一次。
import { useQueries } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { useActor } from './actor-store.ts';
import { api } from './api.ts';

export const STATUS_DICT_TYPE = {
  fund: 'fund_status',
  project: 'project_status',
  investor: 'investor_status',
  party: 'party_status',
  kyc: 'kyc_status',
} as const;

export type StatusDictResource = keyof typeof STATUS_DICT_TYPE;

const TYPES = Object.values(STATUS_DICT_TYPE);

interface DictRow {
  code: string;
  label_zh: string | null;
  label_en: string | null;
}

let labels: Record<string, Record<string, string>> = {};
let version = 0;
const listeners = new Set<() => void>();

/** 字典裡這個狀態的標籤；字典沒有 (或還沒載入) 回 undefined, 由呼叫端退回備援。 */
export function dictStatusLabel(resource: string | undefined, status: string): string | undefined {
  if (!resource || !(resource in STATUS_DICT_TYPE)) return undefined;
  return labels[STATUS_DICT_TYPE[resource as StatusDictResource]]?.[status];
}

function publish(type: string, rows: DictRow[]) {
  const next: Record<string, string> = {};
  for (const r of rows) {
    const label = r.label_zh ?? r.label_en;
    if (label) next[r.code] = label;
  }
  const prev = labels[type] ?? {};
  const same =
    Object.keys(prev).length === Object.keys(next).length &&
    Object.entries(next).every(([k, v]) => prev[k] === v);
  if (same) return;
  labels = { ...labels, [type]: next };
  version += 1;
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** 字典回來 / 有人改了標籤時讓元件重新 render。 */
export function useStatusDictVersion(): number {
  return useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
}

/** 掛在 Layout 一次 —— 抓五份狀態字典寫進 store。 不 render 任何東西。 */
export function StatusDictionaryLoader() {
  const actor = useActor((s) => s.actor);
  const qs = useQueries({
    queries: TYPES.map((type) => ({
      queryKey: ['ref', type, false, '', actor],
      queryFn: () =>
        api<DictRow[]>(
          `/api/v1/master-data?type=${encodeURIComponent(type)}&include_inactive=false`,
          { actor },
        ),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const stamp = qs.map((q) => q.dataUpdatedAt).join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: stamp 就是 qs 資料有沒有變的指紋
  useEffect(() => {
    TYPES.forEach((type, i) => {
      const data = qs[i]?.data;
      if (data) publish(type, data);
    });
  }, [stamp]);
  return null;
}
